import React from "react";
import {
  StyledIconButton,
  PrimaryButton,
} from "../../components/StyledComponents/Buttons/AuradineButtons.js";
import {
  StyledDialog,
  StyledDialogTitle,
} from "../../components/StyledComponents/Dialog/Dialog.js";
import { useTheme } from "../../ThemeContext/ThemeContext.js";
import {
  H5HeadlineMedium,
  H8TitleMedium,
  BodyMedium,
} from "../../components/StyledComponents/Typography/Typography.tsx";
import {
  CircularProgress,
  DialogContent,
  FormControl,
  Stack,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  FormControlLabel,
} from "@mui/material";
import { StyledMenuItem } from "../../components/StyledComponents/Menu/Menu.tsx";
import { StyledFormControl, StyledSelect } from "../../components/StyledComponents/Inputs/Inputs.js";
import { Controller, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import {
  StyledTextField,
  StyledCheckbox,
} from "../../components/StyledComponents/Inputs/Inputs.js";
import useAppContextProvider from "../../AppContext/useAppContextProvider.js";
import { useCaseCommands } from "../queries/useCaseCommands.ts";

const CreateCaseModal = (props) => {
  const { open, setOpen, refetch, setTableKey, prePopulatedSerialNumbers, prePopulatedSerialType } = props;
  const { setToastMessage, setToastOpen } = useAppContextProvider();
  const { colors } = useTheme();

  // Ref to track if we've set the default value for generateTechsupport
  const hasSetDefaultRef = React.useRef(false);

  const {
    mutate: sendCaseCommands,
    isPending,
    isSuccess,
  } = useCaseCommands();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      issue: "",
      description: "",
      summary: "",
      serialType: "chassis", // Always chassis since Others field always shows chassis numbers
      serialNo: "",
      generateTechsupport: true, // Default to ON
    },
    mode: "onChange",
  });

  const issueValue = watch("issue");
  const descriptionValue = watch("description");
  const summaryValue = watch("summary");
  const serialTypeValue = watch("serialType");
  const serialNoValue = watch("serialNo");

  // Check if fields should be disabled based on pre-populated data
  // Disable when there's actual pre-populated data from Miner Inventory
  const hasPrePopulatedData = prePopulatedSerialNumbers &&
                             prePopulatedSerialNumbers.trim() !== "" &&
                             prePopulatedSerialNumbers !== undefined;

  const isTypeDisabled = hasPrePopulatedData;
  const isSerialNoDisabled = hasPrePopulatedData;

  // Check if serials are not empty (either user entered or pre-populated) and type is not "none"
  const hasSerials = serialTypeValue !== 'none' && ((serialNoValue && serialNoValue.trim() !== "") || hasPrePopulatedData);

  // Helper function to get the appropriate label based on serial type
  const getSerialTypeLabel = (type) => {
    switch (type) {
      case 'chassis':
        return 'Chassis Serial Numbers';
      case 'controlBoard':
        return 'Control Board Serial Numbers';
      default:
        return 'Others';
    }
  };

  const onSubmit = (data) => {
    const { issue, description, summary, serialType, serialNo, generateTechsupport } = data;

    // Debug logging for summary field
    console.log('CreateCaseModal - Form data received:', data);
    console.log('CreateCaseModal - Summary value:', summary);
    console.log('CreateCaseModal - Summary type:', typeof summary);
    console.log('CreateCaseModal - Summary length:', summary?.length);
    console.log('CreateCaseModal - Summary trimmed:', summary?.trim());

    // Convert serialNo to array format if it's a string
    let serialNoArray = [];
    if (serialNo && typeof serialNo === 'string' && serialNo.trim() !== "") {
      serialNoArray = serialNo
        .split('\n')
        .map(sn => sn.trim())
        .filter(sn => sn.length > 0);
    } else if (Array.isArray(serialNo)) {
      serialNoArray = serialNo.filter(sn => sn && sn.trim().length > 0);
    }

    console.log('CreateCaseModal - Original data:', { issue, description, summary, serialType, serialNo });
    console.log('CreateCaseModal - Processed serialNoArray:', serialNoArray);

    // Create the case command payload
    const caseCommandPayload = {
      command: "create-case",
      issue: issue,
      description: description,
      summary: summary,
      serialType: serialType,
      serialNo: serialNoArray,
      generateTechsupport: generateTechsupport || false, // Include checkbox value (true if checked, false if unchecked)
    };

    console.log('CreateCaseModal - Final payload being sent:', caseCommandPayload);
    console.log('CreateCaseModal - Summary in payload:', caseCommandPayload.summary);

    sendCaseCommands(
      {
        commands: caseCommandPayload,
      },
      {
        onSuccess: async (response) => {
          if (response.status === "ok") {
            await refetch();
            setTableKey((prev) => prev + 1);
            setOpen(false);
            setToastMessage("Case created successfully");
            setToastOpen(true);
            reset();
          } else {
            setOpen(false);
            setToastMessage(`Error creating case1: ${response.msg}`);
            setToastOpen(true);
          }
        },
        onError: (error) => {
          setToastMessage("Error in creating case: " + error.message);
          setToastOpen(true);
        },
      }
    );
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  // Reset form to default values when modal opens
  React.useEffect(() => {
    if (open) {
      // If pre-populated data is provided (from Miner Inventory), use the provided type; otherwise use defaults
      const hasPrePopulatedData = prePopulatedSerialNumbers &&
                                 prePopulatedSerialNumbers.trim() !== "" &&
                                 prePopulatedSerialNumbers !== undefined;
      // Always set to "chassis" since chassis numbers are always displayed in Others field
      const initialSerialType = "chassis";
      const initialSerialNo = hasPrePopulatedData ? prePopulatedSerialNumbers : "";

      // Reset refs when modal opens
      hasSetDefaultRef.current = false;
      prevHasSerialsRef.current = false;

      reset({
        issue: "",
        description: "",
        summary: "",
        serialType: initialSerialType,
        serialNo: initialSerialNo,
        generateTechsupport: true, // Default to ON
      });
    }
  }, [open, reset, prePopulatedSerialNumbers, prePopulatedSerialType]);

  // Set generateTechsupport to true by default when checkbox first appears (serials are entered)
  const prevHasSerialsRef = React.useRef(false);
  React.useEffect(() => {
    const serialsJustBecameAvailable = hasSerials && !prevHasSerialsRef.current;

    if (serialTypeValue !== 'none' && serialsJustBecameAvailable && !hasSetDefaultRef.current) {
      // When serials first become available, set checkbox to true by default
      setValue('generateTechsupport', true, { shouldValidate: false });
      hasSetDefaultRef.current = true;
    } else if (serialTypeValue === 'none' || !hasSerials) {
      // Reset the ref when serials are cleared or type is "none"
      hasSetDefaultRef.current = false;
    }

    // Update previous value
    prevHasSerialsRef.current = hasSerials;
  }, [hasSerials, serialTypeValue, setValue]);

  return (
    <StyledDialog
      colors={colors}
      maxWidth={"md"}
      fullWidth
      open={open}
      hideBackdrop
    >
      <StyledDialogTitle sx={{ paddingTop: 0 }}>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
        >
          <H5HeadlineMedium style={{ textAlign: "left" }}>
            Create New Case
          </H5HeadlineMedium>
          <StyledIconButton
            colors={colors}
            onClick={handleClose}
            style={{ textAlign: "right" }}
          >
            <CloseIcon  />
          </StyledIconButton>
        </Box>
      </StyledDialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <form onSubmit={handleSubmit(onSubmit)} key={`form-${open}-${prePopulatedSerialNumbers}`}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={8}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={4}
              className="case-details-container"
            >
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <H8TitleMedium>Case Information</H8TitleMedium>
              </Box>

              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl colors={colors} fullWidth margin="none">
                  <Controller
                    name="issue"
                    control={control}
                    rules={{
                      required: "Category is required",
                    }}
                    render={({ field }) => (
                      <StyledFormControl colors={colors} fullWidth>
                        <InputLabel id="issue-summary-label">Category</InputLabel>
                        <StyledSelect
                          {...field}
                          labelId="issue-summary-label"
                          label="Category"
                          variant="outlined"
                          error={Boolean(errors.issue)}
                          colors={colors}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: "250px",
                                overflowY: "auto",
                                backgroundColor: colors?.background?.paper,
                                color: colors?.text?.primary,
                              },
                            },
                          }}
                        >
                          <StyledMenuItem colors={colors} value="Administration/Management">Administration/Management</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="API">API</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="Power Supply">Power Supply</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="Fans Chassis">Fans Chassis</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="PSU Fans">PSU Fans</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="Control Board">Control Board</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="Hash Board">Hash Board</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="Configuration">Configuration</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="Network/Connectivity">Network/Connectivity</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="Performance">Performance</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="Tuning">Tuning</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="Miner UI">Miner UI</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="FluxVision">FluxVision</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="ZTP">ZTP</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="General Query">General Query</StyledMenuItem>
                          <StyledMenuItem colors={colors} value="Chassis/System">Chassis/System</StyledMenuItem>
                        </StyledSelect>
                        {errors.issue && (
                          <FormHelperText error>
                            {errors.issue.message}
                          </FormHelperText>
                        )}
                      </StyledFormControl>
                    )}
                  />
                </StyledFormControl>
              </Box>

              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl colors={colors} fullWidth margin="none">
                  <Controller
                    name="summary"
                    control={control}
                    rules={{
                      required: "Summary is required",
                      minLength: {
                        value: 5,
                        message: "Summary must be at least 5 characters",
                      },
                      maxLength: {
                        value: 255,
                        message: "Summary must be less than 255 characters",
                      },
                    }}
                    render={({ field }) => {
                      console.log('CreateCaseModal - Summary field render:', {
                        value: field.value,
                        onChange: field.onChange,
                        onBlur: field.onBlur,
                        name: field.name
                      });
                      return (
                        <StyledTextField colors={colors}
                          {...field}
                          variant="outlined"
                          label="Summary"
                          fullWidth
                          placeholder="Brief summary of the issue"
                          error={Boolean(errors.summary)}
                          helperText={
                            errors.summary
                              ? errors.summary.message
                              : `Brief summary of the issue (${200 - (summaryValue?.length || 0)} characters remaining)`
                          }
                        />
                      );
                    }}
                  />
                </StyledFormControl>
              </Box>

              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl colors={colors} fullWidth margin="none">
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      required: "Description is required",
                      minLength: {
                        value: 10,
                        message: "Description must be at least 10 characters",
                      },
                      maxLength: {
                        value: 1000,
                        message: "Description must be less than 1000 characters",
                      },
                    }}
                    render={({ field }) => (
                      <StyledTextField colors={colors}
                        {...field}
                        variant="outlined"
                        label="Description"
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Provide detailed description of the issue, steps to reproduce, and any relevant information"
                        error={Boolean(errors.description)}
                        helperText={
                          errors.description
                            ? errors.description.message
                            : `Detailed description of the issue and relevant information (${1000 - (descriptionValue?.length || 0)} characters remaining)`
                        }
                      />
                    )}
                  />
                </StyledFormControl>
              </Box>

              <Box
                display="flex"
                flexDirection="column"
                gap={4}
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  flexGrow={1}
                >
                  <StyledFormControl colors={colors} fullWidth margin="none">
                    <Controller
                      name="serialType"
                      control={control}
                      rules={{
                        required: isTypeDisabled ? false : "Serial number type is required",
                      }}
                      render={({ field }) => (
                        <StyledFormControl colors={colors} fullWidth>
                          <InputLabel id="serial-number-type-label">Type</InputLabel>
                          <StyledSelect
                            colors={colors}
                            {...field}
                            labelId="serial-number-type-label"
                            label="Type"
                            variant="outlined"
                            disabled={isTypeDisabled}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  maxHeight: "250px",
                                  overflowY: "auto",
                                  backgroundColor: colors?.background?.paper,
                                  color: colors?.text?.primary,
                                },
                              },
                            }}
                          >
                            <StyledMenuItem colors={colors} value="none">None</StyledMenuItem>
                            <StyledMenuItem colors={colors} value="chassis">Chassis Serial Numbers</StyledMenuItem>
                            {/* <StyledMenuItem colors={colors} value="controlBoard">Control Board Serial Numbers</StyledMenuItem> */}
                          </StyledSelect>
                        </StyledFormControl>
                      )}
                    />
                  </StyledFormControl>
                </Box>

                {serialTypeValue !== 'none' && (
                  <Box
                    display="flex"
                    flexDirection="row"
                    flexGrow={1}
                  >
                    <StyledFormControl colors={colors} fullWidth margin="none">
                      <Controller
                        name="serialNo"
                        control={control}
                        rules={{
                          validate: (value) => {
                            // If field is disabled (pre-populated), no validation needed
                            if (isSerialNoDisabled) {
                              return true;
                            }
                            if (serialTypeValue === 'none') {
                              return true; // Not required when "None" is selected
                            }

                            // serialNo is required when type is not "none"
                            if (!value || value.trim() === "") {
                              const serialTypeLabel = getSerialTypeLabel(serialTypeValue);
                              return `Please enter at least one ${serialTypeLabel}`;
                            }

                            const serialTypeLabel = getSerialTypeLabel(serialTypeValue);
                            const lines = value.split('\n').filter(line => line.trim() !== '');

                            // Check if there are any valid lines
                            if (lines.length === 0) {
                              return `Please enter at least one ${serialTypeLabel}`;
                            }

                            // If user provided serial numbers, validate them
                            if (lines.length > 500) {
                              return `Maximum 500 ${serialTypeLabel} allowed`;
                            }

                            return true;
                          },
                        }}
                        render={({ field }) => (
                          <StyledTextField colors={colors}
                            {...field}
                            variant="outlined"
                            label={getSerialTypeLabel(serialTypeValue)}
                            fullWidth
                            multiline
                            rows={6}
                            placeholder={`Enter ${getSerialTypeLabel(serialTypeValue)}, one per line`}
                            error={Boolean(errors.serialNo)}
                            helperText={
                              errors.serialNo
                                ? errors.serialNo.message
                                : `Enter ${getSerialTypeLabel(serialTypeValue)} affected by this issue (${serialNoValue?.split('\n').filter(line => line.trim() !== '').length || 0}/500 lines)`
                            }
                            disabled={isSerialNoDisabled}
                          />
                        )}
                      />
                    </StyledFormControl>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Generate Techsupport checkbox - only show when serials are not empty and type is not "none" */}
            {hasSerials && (
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"flex-start"}
                alignItems={"center"}
                className="tech-support-checkbox-container"
              >
                <Controller
                  name="generateTechsupport"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <StyledCheckbox
                          {...field}
                          checked={field.value || false}
                          colors={colors}
                        />
                      }
                      label={
                        <BodyMedium>
                          Generate Techsupport file for Customer Success Team
                        </BodyMedium>
                      }
                    />
                  )}
                />
              </Box>
            )}

            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
              gap={4}
              className="action-button-container"
            >
              <PrimaryButton colors={colors}
                type="submit"
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid || isPending}
                endIcon={
                  isPending && (
                    <CircularProgress
                      size={24}
                      sx={{ color: colors?.primary?.main }}
                    />
                  )
                }
              >
                {isPending
                  ? "Creating..."
                  : "Create Case"}
              </PrimaryButton>
            </Box>
          </Box>
        </form>
      </DialogContent>
    </StyledDialog>
  );
};

export default CreateCaseModal;
